import React, { useEffect, useRef } from 'react';
import {
  TeamSubscriptionSummary,
  TeamSubscriptionTrackerItem,
} from '../../../types/admin/subscriptionTracker';
import SeatMembersHover from './SeatMembersHover';
import { InlineTeamPlanDetails } from './InlineUserPlanDetails';

interface TeamSubTableProps {
  teams: TeamSubscriptionTrackerItem[];
  teamSummaries: Record<string, TeamSubscriptionSummary>;
  loadingTeamIds?: Record<number, boolean>;
  pendingTeamIds?: Record<number, boolean>;
  onRequestTeamSummary: (teamId: number) => void;
  onViewUsers: (team: TeamSubscriptionTrackerItem) => void;
}

function resolveTeamSummary(
  teamSummaries: Record<string, TeamSubscriptionSummary>,
  teamId: number
): TeamSubscriptionSummary | undefined {
  return teamSummaries[String(teamId)] ?? (teamSummaries as Record<number, TeamSubscriptionSummary>)[teamId];
}

function shouldSkipSummaryRequest(
  teamId: number,
  teamSummaries: Record<string, TeamSubscriptionSummary>,
  loadingTeamIds: Record<number, boolean>,
  pendingTeamIds: Record<number, boolean>
): boolean {
  if (resolveTeamSummary(teamSummaries, teamId)) return true;
  if (loadingTeamIds[teamId]) return true;
  if (pendingTeamIds[teamId]) return true;
  return false;
}

const TeamSubTable: React.FC<TeamSubTableProps> = ({
  teams,
  teamSummaries,
  loadingTeamIds = {},
  pendingTeamIds = {},
  onRequestTeamSummary,
  onViewUsers,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const onRequestRef = useRef(onRequestTeamSummary);
  onRequestRef.current = onRequestTeamSummary;

  const teamSummariesRef = useRef(teamSummaries);
  teamSummariesRef.current = teamSummaries;
  const loadingTeamIdsRef = useRef(loadingTeamIds);
  loadingTeamIdsRef.current = loadingTeamIds;
  const pendingTeamIdsRef = useRef(pendingTeamIds);
  pendingTeamIdsRef.current = pendingTeamIds;

  useEffect(() => {
    const root = containerRef.current;
    if (!root || teams.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target as HTMLElement;
          const teamId = Number(el.dataset.teamId);
          if (!Number.isFinite(teamId)) continue;

          if (
            shouldSkipSummaryRequest(
              teamId,
              teamSummariesRef.current,
              loadingTeamIdsRef.current,
              pendingTeamIdsRef.current
            )
          ) {
            if (resolveTeamSummary(teamSummariesRef.current, teamId)) {
              observer.unobserve(el);
            }
            continue;
          }

          onRequestRef.current(teamId);
        }
      },
      { root: null, rootMargin: '200px', threshold: 0 }
    );

    const sections = root.querySelectorAll<HTMLElement>('section[data-team-id]');
    sections.forEach((section) => {
      const teamId = Number(section.dataset.teamId);
      if (Number.isFinite(teamId) && resolveTeamSummary(teamSummaries, teamId)) {
        return;
      }
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, [teams, teamSummaries]);

  if (teams.length === 0) {
    return (
      <div className="text-center p-12 bg-gray-50 rounded-lg text-gray-500">
        No teams match your search.
      </div>
    );
  }

  return (
    <div ref={containerRef} className="space-y-4">
      {teams.map((team) => {
        const summary = resolveTeamSummary(teamSummaries, team.team_id);
        const teamLoading = Boolean(loadingTeamIds[team.team_id]);
        const teamPending = Boolean(pendingTeamIds[team.team_id]);
        const activeSubs = summary?.active_sub_count ?? 0;
        const accentClass =
          teamLoading || teamPending
            ? 'before:bg-slate-300'
            : activeSubs > 0
              ? 'before:bg-emerald-500'
              : 'before:bg-rose-400';

        return (
          <section
            key={team.team_id}
            data-team-id={team.team_id}
            className={`relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm before:absolute before:inset-y-0 before:left-0 before:w-1 ${accentClass}`}
          >
            <div className="px-4 py-4 pl-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <h3 className="text-lg font-bold text-gray-900">{team.team_name}</h3>
                <div className="text-sm text-gray-600 shrink-0">
                  <span className="font-medium text-gray-700">Seats: </span>
                  <SeatMembersHover teamId={team.team_id}>
                    <span className="tabular-nums">
                      {team.seats_occupied}/{team.seat_limit}
                    </span>
                  </SeatMembersHover>
                </div>
              </div>

              <InlineTeamPlanDetails
                summary={summary}
                summariesLoading={teamLoading}
                summaryPending={!summary && teamPending}
              />

              <div className="mt-4 flex justify-end border-t border-gray-100 pt-3">
                <button
                  type="button"
                  onClick={() => onViewUsers(team)}
                  className="px-3 py-1.5 text-sm font-medium text-violet-600 border border-violet-200 rounded-md hover:bg-violet-50"
                >
                  View users
                </button>
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default TeamSubTable;
